/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    dynamicWidth?: boolean;
    extendedStyles?: SelectPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const ReadOnlyContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 15px;
`;

export const productUnitOfMeasureSelectStyles: SelectPresentationProps = {};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures, isVariantParent },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    dynamicWidth,
    productSettings,
    labelOverride,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));

    const calculateWidth = (length: number): string => {
        return `${8 * length + 35}px`;
    };

    const findWidth = () => {
        const uom = unitOfMeasure
            ? unitOfMeasures?.find(uom => uom.unitOfMeasure === unitOfMeasure)
            : unitOfMeasures?.find(uom => uom.isDefault);
        if (uom) {
            const additionLength = uom.qtyPerBaseUnitOfMeasure !== 1 ? `/ ${uom.qtyPerBaseUnitOfMeasure}`.length : 0;
            return calculateWidth(uom.description.length + additionLength);
        }
        return "fit-content";
    };

    const [width, setWidth] = React.useState(dynamicWidth ? findWidth() : "100%");

    if (isVariantParent) {
        return null;
    }

    if (
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
        ) {
        return (<><ReadOnlyContainer style={{marginTop: 0}}>
            <Typography size={15} weight={600} color={UCSColors.gray04} transform="uppercase">{translate("Unit of Measure")}</Typography>
            <Typography style={{marginTop: "18px"}} color={UCSColors.gray04}>{unitOfMeasure}</Typography>
        </ReadOnlyContainer></>);
    }


    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    return (
        <Select
            label={labelOverride ?? translate("Unit of Measure")}
            value={unitOfMeasure}
            onChange={uomChangeHandler}
            disabled={disabled}
            data-test-selector="product_unitOfMeasureSelect"
            {...styles}
            {...otherProps}
            style={{width: `${width}`}}  
        >
            {unitOfMeasures.map(uom => (
                <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                    {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                    {/* {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""} */}
                </option>
            ))}
        </Select>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
